import { LanguageDialect } from '@ftr/contracts/type/recording'

export type LaunchDarklyFlagValue = boolean | number | string | Record<string, unknown>

export interface HardwareClockIntervals extends Record<string, unknown> {
  inSessionMs: number
  outOfSessionMs: number
}

export const DEFAULT_HARDWARE_CLOCK_INTERVALS: HardwareClockIntervals = {
  inSessionMs: 0,
  outOfSessionMs: 0,
}

export interface ReportDataConfiguration extends Record<string, unknown> {
  asyncExportEnabled: boolean
}

export const DEFAULT_REPORT_DATA_CONFIGURATION: ReportDataConfiguration = {
  asyncExportEnabled: false,
}

export type DisplaySttMode = 'stt' | 'translation'

export interface TranslationDebugTools {
  tags: boolean
  summary: boolean
}

export interface TranslationSettings extends Record<string, unknown> {
  courtSystemDialect?: LanguageDialect
  /** What the court's language is translated into. todo: will come from courtroom settings similar to dialect, eventually */
  targetLanguage?: string
  display?: {
    mode: DisplaySttMode
    debug: TranslationDebugTools
  }
}

export const DEFAULT_TRANSLATION_SETTINGS: TranslationSettings = {}

export interface WebFeatureFlags {
  'client-side-report-pdf-export': boolean
  'direct-upload': boolean
  'direct-upload-multi-files': boolean
  'direct-upload-time-range-validation': boolean
  'max-direct-upload-files': number // See LD for what values map back to
  'external-direct-upload': boolean
  'design-system-overlay': boolean
  'detailed-error-messages': boolean
  'disable-chromium-remapping-hack': boolean
  'feature-annotations-shortcuts': boolean
  'feature-automatic-annotations': string
  'feature-disable-channel-remapping': boolean
  'filter-recordings-list-by-on-the-record-content': boolean
  'persistent-flag-switch': boolean
  'real-time-stt-attribution-approach':
    | 'never-use-software-based-attribution'
    | 'court-system-configures-attribution-per-room'
    | 'always-use-software-based-attribution'
  'realtime-recording-automated-deletion': boolean
  'realtime-recording-on-demand-deletion': boolean
  'recording-vault-activity-log-liveness': boolean
  'recording-vault-support': boolean
  'report-trm-average-upload-time': boolean
  'report-number-of-trm-converted': boolean
  'report-trm-uploads-list': boolean
  'revoke-refresh-token': boolean
  'rth-defined-channel-audio-emission': number // 0 = hybrid recorder emission disabled
  'rth-display-debug': boolean
  'rth-display-clock-mode': boolean
  'display-app-hardware-clock-update-intervals': HardwareClockIntervals
  'rth-enabled-realtime-hearings': boolean
  'rth-feature-show-infrastructure-status': boolean
  'rth-hybrid-audio-routing': boolean
  'rth-join-courtroom-remotely': boolean
  'rth-schedule-proceedings': boolean
  'standard-stt-delay-seconds': number
  'stt-debug-ui-tooling': boolean
  'stt-licensing-terms': boolean
  // leave this one for unit tests
  'test-boolean-flag': boolean
  'trm-recording-channel-map-strategy': number // See LD for what values map back to
  'courtroom-monitoring-overview-actions-menu': boolean
  'courtroom-monitoring-glance-metrics-filtering': boolean
  'ordering-reports': boolean
  'public-navigation': boolean
  'feature-hearing-summaries': boolean
  'hls-playback-start-stop-hls-loading': boolean
  'recording-session-sealing-api': boolean
  'ftr-forum': boolean
  'video-licencing': boolean
  'rth-upgrader-v2': boolean // Recorder 3.0 feature flag
  'enable-desktop-web-version-checks': boolean
  'feature-case-summaries': boolean
  'realtime-recorder-list-with-pagination': boolean
  'rtstt-name-corrections': boolean
  'feature-channel-layouts': boolean
  'feature-out-of-session-content-handling': boolean
  'feature-speaker-attribution': boolean
  'debug-tools': boolean
  'amplitude-analytics': boolean
  'feature-annotations-uh-fields': boolean
  'courtroom-show-local-recording-status': boolean
  'feature-annotations-events-court-system-filter': boolean
  'feature-annotations-events-filtering': { blacklist: string[] }
  'ftr-reports-report-data-configuration': ReportDataConfiguration
  'audio-segment-lookup-from-regional-first': boolean
  'feature-translation': boolean
  'translation-settings': TranslationSettings
  'individual-subscription': boolean
  'feature-annotations-upcoming-hearing-ui-crud': boolean
}

export type FeatureFlagKeyOfType<TValue> = Exclude<
  keyof {
    [K in keyof WebFeatureFlags as WebFeatureFlags[K] extends TValue ? K : never]: never
  },
  symbol | number
>

/* Defined here to avoid circular dependency with real-time-stt.component.ts */
export const DEFAULT_STANDARD_STT_DELAY_SECONDS = 240

export const defaultFlags: WebFeatureFlags = {
  'client-side-report-pdf-export': false,
  'direct-upload': false,
  'direct-upload-multi-files': false,
  'direct-upload-time-range-validation': false,
  'max-direct-upload-files': 1,
  'external-direct-upload': false,
  'design-system-overlay': false,
  'detailed-error-messages': false,
  'disable-chromium-remapping-hack': false,
  'feature-annotations-shortcuts': false,
  'feature-automatic-annotations': 'off',
  'feature-disable-channel-remapping': false,
  'filter-recordings-list-by-on-the-record-content': false,
  'persistent-flag-switch': false,
  'real-time-stt-attribution-approach': 'never-use-software-based-attribution',
  'realtime-recording-automated-deletion': false,
  'realtime-recording-on-demand-deletion': false,
  'recording-vault-activity-log-liveness': false,
  'recording-vault-support': false,
  'report-number-of-trm-converted': false,
  'report-trm-average-upload-time': false,
  'report-trm-uploads-list': false,
  'revoke-refresh-token': false,
  'rth-defined-channel-audio-emission': 0, // 0 = hybrid recorder emission disabled
  'rth-display-debug': false,
  'rth-display-clock-mode': false,
  'display-app-hardware-clock-update-intervals': DEFAULT_HARDWARE_CLOCK_INTERVALS,
  'rth-enabled-realtime-hearings': false,
  'rth-feature-show-infrastructure-status': false,
  'rth-hybrid-audio-routing': false,
  'rth-join-courtroom-remotely': false,
  'rth-schedule-proceedings': false,
  'standard-stt-delay-seconds': DEFAULT_STANDARD_STT_DELAY_SECONDS,
  'stt-debug-ui-tooling': false,
  'stt-licensing-terms': false,
  'test-boolean-flag': false,
  'trm-recording-channel-map-strategy': 0,
  'courtroom-monitoring-overview-actions-menu': false,
  'courtroom-monitoring-glance-metrics-filtering': false,
  'ordering-reports': false,
  'public-navigation': false,
  'feature-hearing-summaries': false,
  'hls-playback-start-stop-hls-loading': false,
  'recording-session-sealing-api': false,
  'ftr-forum': false,
  'video-licencing': false,
  'rth-upgrader-v2': false, // Recorder 3.0 feature flag
  'enable-desktop-web-version-checks': false,
  'feature-case-summaries': false,
  'realtime-recorder-list-with-pagination': false,
  'rtstt-name-corrections': false,
  'feature-channel-layouts': false,
  'feature-out-of-session-content-handling': false,
  'feature-speaker-attribution': false,
  'debug-tools': false,
  'amplitude-analytics': false,
  'courtroom-show-local-recording-status': false,
  'feature-annotations-uh-fields': false,
  'feature-annotations-events-court-system-filter': false,
  'feature-annotations-events-filtering': { blacklist: [] },
  'ftr-reports-report-data-configuration': DEFAULT_REPORT_DATA_CONFIGURATION,
  'audio-segment-lookup-from-regional-first': false,
  'feature-translation': false,
  'translation-settings': DEFAULT_TRANSLATION_SETTINGS,
  'individual-subscription': false,
  'feature-annotations-upcoming-hearing-ui-crud': false,
}
